/**
 * BatchOperationCard Component
 *
 * Displays a batch operation with progress bar, counts, and action buttons.
 */

import { useState } from 'react';
import { Play, Pause, X, Eye, Activity, Clock, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import type { BatchOperation } from '@/api/batch-operations';
import { BatchResultsDialog } from './BatchResultsDialog';
import { BatchProgressSheet } from './BatchProgressSheet';

interface BatchOperationCardProps {
  operation: BatchOperation;
  onPause?: (id: string) => void;
  onResume?: (id: string) => void;
  onCancel?: (id: string) => void;
  isPausing?: boolean;
  isResuming?: boolean;
  isCancelling?: boolean;
}

const OPERATION_TYPE_LABELS: Record<string, string> = {
  auto_enrich: 'Auto-Enrich',
  batch_tags: 'Tag Analysis',
  batch_publish: 'Batch Publish',
  generate_descriptions: 'Generate Descriptions',
  bulk_update: 'Bulk Update',
  ai_content: 'AI Content',
  batch_classify_type: 'Classify type, styles & features',
  batch_resync_content: 'Re-sync AI Content',
  account_disable: 'Disable Account',
};

interface StatusBadgeInfo {
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  label: string;
  icon: typeof CheckCircle;
}

const DEFAULT_STATUS_BADGE: StatusBadgeInfo = { variant: 'secondary', label: 'Pending', icon: Clock };

const STATUS_BADGES: Record<string, StatusBadgeInfo> = {
  pending: DEFAULT_STATUS_BADGE,
  running: { variant: 'default', label: 'Running', icon: Play },
  paused: { variant: 'outline', label: 'Paused', icon: Pause },
  completed: { variant: 'secondary', label: 'Completed', icon: CheckCircle },
  failed: { variant: 'destructive', label: 'Failed', icon: XCircle },
  cancelled: { variant: 'outline', label: 'Cancelled', icon: X },
};

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString();
}

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt) return '-';
  const start = new Date(startedAt).getTime();
  const end = completedAt ? new Date(completedAt).getTime() : Date.now();
  const durationMs = end - start;

  const seconds = Math.floor(durationMs / 1000);
  if (seconds < 60) return `${String(seconds)}s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) return `${String(minutes)}m ${String(remainingSeconds)}s`;

  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${String(hours)}h ${String(remainingMinutes)}m`;
}

export function BatchOperationCard({
  operation,
  onPause,
  onResume,
  onCancel,
  isPausing,
  isResuming,
  isCancelling,
}: BatchOperationCardProps): JSX.Element {
  const [showResults, setShowResults] = useState(false);
  const [progressSheetOpen, setProgressSheetOpen] = useState(false);

  const progress = operation.totalCount > 0
    ? Math.round((operation.processedCount / operation.totalCount) * 100)
    : 0;

  const statusBadge = STATUS_BADGES[operation.status] ?? DEFAULT_STATUS_BADGE;
  const StatusIcon = statusBadge.icon;

  const isRunning = operation.status === 'running';
  const isPaused = operation.status === 'paused';
  const isPending = operation.status === 'pending';
  const isActive = isRunning || isPaused || isPending;
  const isFinished = operation.status === 'completed' || operation.status === 'failed' || operation.status === 'cancelled';

  return (
    <>
      <Card data-testid="batch-operation-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <CardTitle className="text-lg" data-testid="batch-operation-title">
                {OPERATION_TYPE_LABELS[operation.operationType] ?? operation.operationType}
              </CardTitle>
              <CardDescription>
                {operation.partnerAccountName ?? `Account #${operation.partnerAccountId}`}
                {operation.locationFilter && (
                  <span className="ml-2 text-muted-foreground">
                    ({operation.locationFilter} locations)
                  </span>
                )}
              </CardDescription>
            </div>
            <Badge variant={statusBadge.variant} data-testid="batch-operation-status">
              <StatusIcon className="mr-1 h-3 w-3" />
              {statusBadge.label}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Progress bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Progress</span>
              <span data-testid="batch-operation-progress">
                {String(operation.processedCount)} / {String(operation.totalCount)} ({String(progress)}%)
              </span>
            </div>
            <Progress value={progress} data-testid="batch-operation-progress-bar" />
          </div>

          {/* Counts */}
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="flex items-center gap-2" data-testid="batch-operation-success-count">
              <CheckCircle className="h-4 w-4 text-green-500" />
              <span>{String(operation.successCount)} success</span>
            </div>
            <div className="flex items-center gap-2" data-testid="batch-operation-failed-count">
              <XCircle className="h-4 w-4 text-red-500" />
              <span>{String(operation.failedCount)} failed</span>
            </div>
            <div className="flex items-center gap-2" data-testid="batch-operation-skipped-count">
              <AlertTriangle className="h-4 w-4 text-yellow-500" />
              <span>{String(operation.skippedCount)} skipped</span>
            </div>
          </div>

          {/* Duration */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>
              Started: {formatDate(operation.startedAt)}
              {operation.completedAt && (
                <> | Completed: {formatDate(operation.completedAt)}</>
              )}
              {!isFinished && operation.startedAt && (
                <> | Duration: {formatDuration(operation.startedAt, operation.completedAt)}</>
              )}
              {isFinished && operation.startedAt && operation.completedAt && (
                <> | Total: {formatDuration(operation.startedAt, operation.completedAt)}</>
              )}
            </span>
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2">
            {isRunning && onPause && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { onPause(operation.id); }}
                disabled={isPausing}
                data-testid="batch-operation-pause-btn"
              >
                <Pause className="mr-1 h-4 w-4" />
                {isPausing ? 'Pausing...' : 'Pause'}
              </Button>
            )}

            {isPaused && onResume && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { onResume(operation.id); }}
                disabled={isResuming}
                data-testid="batch-operation-resume-btn"
              >
                <Play className="mr-1 h-4 w-4" />
                {isResuming ? 'Resuming...' : 'Resume'}
              </Button>
            )}

            {(isRunning || isPaused) && onCancel && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={isCancelling}
                    data-testid="batch-operation-cancel-btn"
                  >
                    <X className="mr-1 h-4 w-4" />
                    {isCancelling ? 'Cancelling...' : 'Cancel'}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancel Batch Operation?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will stop the batch operation and remove all pending jobs.
                      Locations already processed will keep their changes.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Keep Running</AlertDialogCancel>
                    <AlertDialogAction onClick={() => { onCancel(operation.id); }}>
                      Cancel Operation
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}

            {isActive && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setProgressSheetOpen(true); }}
                data-testid="batch-operation-view-progress-btn"
              >
                <Activity className="mr-1 h-4 w-4" />
                View Progress
              </Button>
            )}

            {isFinished && operation.results && operation.results.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowResults(true); }}
                data-testid="batch-operation-view-results-btn"
              >
                <Eye className="mr-1 h-4 w-4" />
                View Results
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <BatchResultsDialog
        operation={operation}
        open={showResults}
        onOpenChange={setShowResults}
      />

      <BatchProgressSheet
        operationId={operation.id}
        open={progressSheetOpen}
        onOpenChange={setProgressSheetOpen}
      />
    </>
  );
}
